package io.madrona.njord.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class ColorTuple(
    val color: Color,
    val hex: String,
)


@Serializable
data class Colors(
    val library: Map<ThemeMode, Map<Color, String>>,
    val custom: Map<String, Map<ThemeMode, Map<Color, String>>>
)

@Serializable
enum class ThemeMode: Theme {
    @SerialName("DAY") Day,
    @SerialName("DUSK") Dusk,
    @SerialName("NIGHT") Night;
}

@Serializable
data class CustomTheme(
    val mode: ThemeMode,
    val name: String
) : Theme

@Serializable
sealed interface Theme

fun Theme.colorName() : String {
    return when (this) {
        is CustomTheme -> this.name
        is ThemeMode -> "Default"
    }
}

fun Theme.mode() : ThemeMode {
   return when (this) {
       is CustomTheme -> this.mode
       is ThemeMode -> this
   }
}

fun stylePath(theme: Theme, depth: Depth): String {
    val tail = when(theme) {
        is CustomTheme -> "${theme.mode.name.lowercase()}?c=${theme.name}"
        is ThemeMode -> theme.name.lowercase()
    }
    return "/v1/style/${depth.name.lowercase()}/$tail"
}

@Serializable
enum class Color {
    NODTA,
    CURSR,
    CHBLK,
    CHGRD,
    CHGRF,
    CHRED,
    CHGRN,
    CHYLW,
    CHMGD,
    CHMGF,
    CHBRN,
    CHWHT,
    SCLBR,
    CHCOR,
    LITRD,
    LITGN,
    LITYW,
    ISDNG,
    DNGHL,
    TRFCD,
    TRFCF,
    LANDA,
    LANDF,
    CSTLN,
    SNDG1,
    SNDG2,
    DEPSC,
    DEPCN,
    DEPDW,
    DEPMD,
    DEPMS,
    DEPVS,
    DEPIT,
    RADHI,
    RADLO,
    ARPAT,
    NINFO,
    RESBL,
    ADINF,
    RESGR,
    SHIPS,
    PSTRK,
    SYTRK,
    PLRTE,
    APLRT,
    UINFD,
    UINFF,
    UIBCK,
    UIAFD,
    UINFR,
    UINFG,
    UINFO,
    UINFB,
    UINFM,
    UIBDR,
    UIAFF,
    OUTLW,
    OUTLL,
    RES01,
    RES02,
    RES03,
    BKAJ1,
    BKAJ2,
    MARBL,
    MARCY,
    MARMG,
    MARWH,
}

@Serializable
enum class Sprite {
    ACHARE02,
    ACHARE51,
    ACHBRT07,
    ACHRES51,
    AIRARE02,
    BCNCAR01,
    BCNCAR02,
    BCNCAR03,
    BCNCAR04,
    BCNDEF13,
    BCNISD21,
    BCNLAT15,
    BCNLAT16,
    BCNLAT21,
    BCNLAT22,
    BCNSAW13,
    BCNSAW21,
    BCNSPP13,
    BCNSPP21,
    BOYCAR01,
    BOYCAR02,
    BOYCAR03,
    BOYCAR04,
    BOYDEF03,
    BOYISD12,
    BOYLAT13,
    BOYLAT14,
    BOYLAT23,
    BOYLAT24,
    BOYMOR11,
    BOYSAW12,
    BOYSPP11,
    BOYSPP15,
    BOYSPP25,
    BOYSPP35,
    BOYSUP02,
    BRIDGE01,
    BRTHNO01,
    BUAARE02,
    BUIREL01,
    BUIREL04,
    BUIREL05,
    BUIREL13,
    BUIREL14,
    BUIREL15,
    BUISGL01,
    BUISGL11,
    CAIRNS01,
    CAIRNS11,
    CBLARE51,
    CGUSTA02,
    CHIMNY01,
    CHIMNY11,
    CHINFO06,
    CHINFO07,
    CHINFO08,
    CHINFO09,
    CHINFO10,
    CHINFO11,
    CHKPNT01,
    CRANES01,
    CTYARE51,
    CURDEF01,
    CURENT01,
    DANGER01,
    DANGER02,
    DANGER03,
    DAYSQR01,
    DAYTRI01,
    DAYTRI05,
    DISMAR06,
    DNGHILIT,
    DOMES001,
    DOMES011,
    DRGARE01P,
    DSHAER11,
    DWRUTE51,
    EBBSTR01,
    ENTRES51,
    FLASTK01,
    FLASTK11,
    FLDSTR01,
    FLGSTF01,
    FLTHAZ02,
    FOGSIG01,
    FORSTC01,
    FORSTC11,
    FOULAR01P,
    FOULGND1,
    FRYARE51,
    FSHFAC02,
    FSHFAC03,
    FSHFAC03P,
    FSHFAC04P,
    FSHGRD01,
    FSHHAV01,
    FSHHAV02P,
    GATCON03,
    GATCON04,
    HILTOP01,
    HILTOP11,
    HRBFAC09,
    HULKES01,
    ICEARE04P,
    ISODGR01,
    LIGHTS11,
    LIGHTS12,
    LIGHTS13,
    LIGHTS81,
    LIGHTS82,
    LITDEF11,
    LITFLT02,
    LITVES02,
    LNDARE01,
    LOCMAG01,
    LOWACC01,
    MAGVAR01,
    MARCUL02,
    MARCUL02P,
    MARSHES1P,
    MONUMT12,
    MORFAC03,
    MORFAC04,
    MSTCON04,
    MSTCON14,
    NEWOBJ01,
    NOTBRD11,
    NODATA03P,
    OBSTRN01,
    OBSTRN02,
    OBSTRN03,
    OBSTRN11,
    OFSPLF01,
    PILBOP02,
    PILPNT02,
    POSGEN01,
    POSGEN03,
    POSGEN04,
    PRCARE12,
    PRDINS02,
    PRTSUR01P,
    QUARRY01,
    QUESMRK1,
    QUESMRK1P,
    RACNSP01,
    RADRFL03,
    RASCAN01,
    RASCAN11,
    RCLDEF01,
    RCTLPT52,
    RDOCAL02,
    RDOCAL03,
    RDOSTA02,
    RECDEF51,
    RECTRC55,
    RECTRC56,
    RECTRC57,
    RECTRC58,
    RETRFL02,
    RFNERY11,
    ROLROL01,
    RSCSTA02,
    RTLDEF51,
    RTPBCN02,
    SILBUI01,
    SILBUI11,
    SISTAT03,
    SISTAW03,
    SMCFAC02,
    SNDWAV01P,
    SNDWAV02,
    SPRING02,
    TIDEHT01,
    TIDSTR01,
    TMARDEF1,
    TMARDEF2,
    TMBYRD01,
    TNKCON02,
    TNKCON12,
    TNKFRM11,
    TOPMAR02, TOPMAR08, TOPMAR16, TOPMAR25, TOPMAR32, TOPMAR85,
    TOPMAR04, TOPMAR10, TOPMAR17, TOPMAR26, TOPMAR33, TOPMAR86,
    TOPMAR05, TOPMAR12, TOPMAR18, TOPMAR27, TOPMAR34, TOPMAR87,
    TOPMAR06, TOPMAR13, TOPMAR22, TOPMAR28, TOPMAR36, TOPMAR88,
    TOPMAR07, TOPMAR14, TOPMAR24, TOPMAR30, TOPMAR65, TOPMAR89,
    TOWERS01, TOWERS02, TOWERS03, TOWERS12,
    TSSLPT51,
    TWRDEF51,
    TWRTPT52,
    TWRTPT53,
    UWTROC03,
    UWTROC04,
    VEGATN03P,
    VEGATN04P,
    WATTUR02,
    WEDKLP03,
    WIMCON01,
    WIMCON11,
    WNDFRM61,
    WNDMIL12,
    WRECKS01,
    WRECKS04,
    WRECKS05,
}
